import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Save as SaveIcon,
  PlayArrow as TestIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { apiClient } from '../../services/api';

interface WhatsAppConfigState {
  phoneNumberId: string;
  accessToken: string;
  appSecret: string;
  businessAccountId: string;
  verifyToken: string;
  configured: boolean;
}

interface WhatsAppConfigResponse {
  whatsapp: {
    phoneNumberId: string;
    accessTokenMasked: string;
    appSecretMasked: string;
    businessAccountId: string;
    verifyToken: string;
    configured: boolean;
  };
}

interface TestConnectionResponse {
  verifiedName: string | null;
  displayPhoneNumber: string | null;
  qualityRating: string | null;
}

interface Props {
  salonId: string;
  canWrite: boolean;
}

const WhatsAppApiConfigCard: React.FC<Props> = ({ salonId, canWrite }) => {
  const [draft, setDraft] = useState<WhatsAppConfigState>({
    phoneNumberId: '',
    accessToken: '',
    appSecret: '',
    businessAccountId: '',
    verifyToken: '',
    configured: false,
  });
  const [serverMaskedTokens, setServerMaskedTokens] = useState<{
    accessToken: string;
    appSecret: string;
  }>({ accessToken: '', appSecret: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);
  const [revealAccess, setRevealAccess] = useState(false);
  const [revealSecret, setRevealSecret] = useState(false);

  const load = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<{ data?: WhatsAppConfigResponse }>(
        `/settings/${salonId}/whatsapp`
      );
      const wa = res?.data?.whatsapp;
      if (wa) {
        setDraft({
          phoneNumberId: wa.phoneNumberId ?? '',
          accessToken: '', // masked from server; left blank so user types only when rotating
          appSecret: '',
          businessAccountId: wa.businessAccountId ?? '',
          verifyToken: wa.verifyToken ?? '',
          configured: wa.configured,
        });
        setServerMaskedTokens({
          accessToken: wa.accessTokenMasked ?? '',
          appSecret: wa.appSecretMasked ?? '',
        });
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load WhatsApp config');
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    // Build a partial payload — backend merges with existing settings, so
    // empty fields don't clobber stored ones. Owners can save just the
    // verifyToken first (so Meta webhook registration works) and add real
    // credentials later. At least ONE non-empty field is required.
    const body: Record<string, string> = {};
    if (draft.phoneNumberId.trim()) body.phoneNumberId = draft.phoneNumberId.trim();
    if (draft.accessToken.trim()) body.accessToken = draft.accessToken.trim();
    if (draft.appSecret.trim()) body.appSecret = draft.appSecret.trim();
    if (draft.businessAccountId.trim()) body.businessAccountId = draft.businessAccountId.trim();
    if (draft.verifyToken.trim()) body.verifyToken = draft.verifyToken.trim();

    if (Object.keys(body).length === 0) {
      setError('Type at least one field before saving.');
      return;
    }

    setSaving(true);
    try {
      const res = await apiClient.put<{ data?: WhatsAppConfigResponse }>(
        `/settings/${salonId}/whatsapp`,
        body
      );
      const wa = res?.data?.whatsapp;
      if (wa) {
        setDraft({
          phoneNumberId: wa.phoneNumberId,
          accessToken: '',
          appSecret: '',
          businessAccountId: wa.businessAccountId,
          verifyToken: wa.verifyToken,
          configured: wa.configured,
        });
        setServerMaskedTokens({
          accessToken: wa.accessTokenMasked,
          appSecret: wa.appSecretMasked,
        });
      }
      setSuccess('Saved. Click "Test Connection" to verify Meta accepts the credentials.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setError(null);
    setSuccess(null);
    setTestResult(null);
    setTesting(true);
    try {
      const res = await apiClient.post<{ data?: TestConnectionResponse }>(
        `/settings/${salonId}/whatsapp/test`,
        {}
      );
      if (res?.data) {
        setTestResult(res.data);
        setSuccess('Connection verified — Meta returned a valid response.');
        setTimeout(() => setSuccess(null), 4000);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: '#25D366' }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3, borderRadius: 3, borderTop: '4px solid #25D366' }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <WhatsAppIcon sx={{ color: '#25D366', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#5C2A30' }}>
            WhatsApp Business Cloud API
          </Typography>
          <Chip
            size="small"
            label={draft.configured ? 'Configured' : 'Not configured'}
            sx={{
              ml: 'auto',
              bgcolor: draft.configured ? 'rgba(37,211,102,0.12)' : 'rgba(0,0,0,0.06)',
              color: draft.configured ? '#1FB957' : 'text.secondary',
              fontWeight: 600,
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect your Meta WhatsApp Business account so the salon can send appointment reminders,
          birthday wishes, and campaigns via approved message templates. Get these values from
          Meta Business Manager → WhatsApp → API Setup.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {success}
          </Alert>
        )}
        {testResult && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Verified: <strong>{testResult.verifiedName ?? '—'}</strong>
            {testResult.displayPhoneNumber && ` · ${testResult.displayPhoneNumber}`}
            {testResult.qualityRating && ` · quality: ${testResult.qualityRating}`}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number ID *"
              value={draft.phoneNumberId}
              onChange={(e) => setDraft({ ...draft, phoneNumberId: e.target.value })}
              placeholder="e.g. 123456789012345"
              disabled={!canWrite}
              helperText="Meta-issued ID for the sending number"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Business Account ID"
              value={draft.businessAccountId}
              onChange={(e) => setDraft({ ...draft, businessAccountId: e.target.value })}
              placeholder="WABA ID (optional, used by templates)"
              disabled={!canWrite}
              helperText="Used later for template management"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Access Token *"
              type={revealAccess ? 'text' : 'password'}
              value={draft.accessToken}
              onChange={(e) => setDraft({ ...draft, accessToken: e.target.value })}
              placeholder={
                serverMaskedTokens.accessToken
                  ? `Currently: ${serverMaskedTokens.accessToken}`
                  : 'System user long-lived token'
              }
              disabled={!canWrite}
              helperText="Re-enter to rotate. We never display the stored token."
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={() => setRevealAccess((v) => !v)}>
                    {revealAccess ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="App Secret *"
              type={revealSecret ? 'text' : 'password'}
              value={draft.appSecret}
              onChange={(e) => setDraft({ ...draft, appSecret: e.target.value })}
              placeholder={
                serverMaskedTokens.appSecret
                  ? `Currently: ${serverMaskedTokens.appSecret}`
                  : 'For webhook signature verification'
              }
              disabled={!canWrite}
              helperText="Re-enter to rotate."
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={() => setRevealSecret((v) => !v)}>
                    {revealSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Webhook Verify Token"
              value={draft.verifyToken}
              onChange={(e) => setDraft({ ...draft, verifyToken: e.target.value })}
              placeholder="Random string you also paste into Meta webhook config"
              disabled={!canWrite}
              helperText="Used by Meta to verify your webhook handler. Make up any string."
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Tooltip title={canWrite ? '' : 'You do not have write access to marketing settings'}>
            <span>
              <Button
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                variant="contained"
                onClick={() => void handleSave()}
                disabled={!canWrite || saving}
                sx={{
                  bgcolor: '#25D366',
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#1FB957' },
                }}
              >
                {saving ? 'Saving…' : 'Save credentials'}
              </Button>
            </span>
          </Tooltip>
          <Button
            startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <TestIcon />}
            variant="outlined"
            onClick={() => void handleTest()}
            disabled={!draft.configured || testing}
            sx={{
              borderColor: '#25D366',
              color: '#1FB957',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: '#1FB957', bgcolor: 'rgba(37,211,102,0.08)' },
            }}
          >
            {testing ? 'Testing…' : 'Test connection'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default WhatsAppApiConfigCard;
